import React, { memo, useMemo } from 'react';
import VideoIcon from '@public/images/kol/Play-Big.svg';
import { nonPageTracker } from '@src/adobe-launch/data-layer-provider';
import {
    AllupBetlineFragment,
    BadgesIconListFragment,
    ContentBlockFragment,
    KolProfileBaseFragment,
} from '@graphqlTypes/sitecore';
import { convertArticleInfo } from '@src/hooks/tracker/use-article-tracker';
import { removeflashUrl } from '@src/utils/util';
import { TrackingEvent } from '@src/adobe-launch/types';
import { useTranslation } from 'next-i18next';
import videoStore from '@mobx/stores/video-store';
import { AppLink } from '@components/common/AppLink';
import KolBadgeList from './kol-badge-list';
import { DeepReadonly } from '@src/utils/type-utils';

type Props = {
    AllUpDataItem: AllupBetlineFragment | null;
    type: 'leader' | 'race';
    contentBlock: ContentBlockFragment | null | undefined;
    raceNumbers: number[];
    raceNo: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    jsBridge?: any;
};

type badgeIconFragment = { badgeIconLink?: { targetItem?: BadgesIconListFragment } };

type badgesItemFragment = {
    name: string;
    children: Array<badgeIconFragment>;
};

/** 用于betpick-ALL UP-头部组件 */
const AllUpHeader = ({
    AllUpDataItem,
    type,
    jsBridge,
    contentBlock,
    raceNumbers,
    raceNo,
}: Props) => {
    const { t, i18n } = useTranslation('kol');
    const lang = i18n.language.toLowerCase() === 'en-us';
    const kolData: DeepReadonly<KolProfileBaseFragment | undefined> =
        AllUpDataItem?.kolProfile?.targetItem;

    const KolName = kolData?.name ?? '';

    const titlePrefix = lang ? 'Race ' : '第';
    const titleJoiner = lang ? ' & ' : ',';
    const titleSuffix = lang ? ' - All Up' : ' - 過關';
    const racePart = raceNumbers.slice(0, 3).join(titleJoiner);

    const raceTitle = `${titlePrefix}${racePart}${!lang ? '場' : ''}${titleSuffix} ${
        AllUpDataItem?.fomula?.value ?? ''
    }`;

    // const profile = betpickDetail.leader;

    const videoNoNative = (type?: string) => {
        videoStore.setDefaults();
        type && videoStore.setVideoType(type);
        kolData && videoStore.setkolName(KolName);
        contentBlock && videoStore.setVideoContentBlock(contentBlock);
        videoStore.setraceID(raceNo);
    };

    const videoNative = (videoNativePip: boolean, link: string) => {
        nonPageTracker(
            {
                eventID: TrackingEvent.VideoPlay,
                raceID: raceNo,
                kolName: KolName,
                videoURL: removeflashUrl(link ?? ''),
                eventValue: 'KOLBetpick',
            },
            { article: convertArticleInfo(contentBlock) }
        );
        return jsBridge.appOpenNativePlayer(link, '', videoNativePip);
    };

    const title = useMemo(() => {
        if (type === 'leader') {
            const profileUrl = `/kol-profile/${KolName}/`;
            const badges =
                kolData?.badges
                    .find((badge: DeepReadonly<badgesItemFragment>) => badge.name === 'badges')
                    ?.children.map((b: badgeIconFragment) => b.badgeIconLink?.targetItem) || [];
            return (
                <div className='flex items-center'>
                    <AppLink href={profileUrl}>
                        <img
                            className='w-[35px] h-[35px] mx-1 rounded-full border-[#1F58D8] border object-cover'
                            alt=''
                            loading='lazy'
                            src={kolData?.kolThumbSq?.src || ''}
                        ></img>
                    </AppLink>
                    <div className='px-2'>
                        <div className='leading-[22px]'>
                            <AppLink href={profileUrl}> {kolData?.kolName?.value}</AppLink>
                            <AppLink href={profileUrl}>
                                <KolBadgeList badges={badges}></KolBadgeList>
                            </AppLink>
                        </div>

                        <span className='text-[#0234A6] text-[12px] font-bold'>
                            <AppLink href={profileUrl}>{`${kolData?.score?.value || 0}${t(
                                'betpick.pts'
                            )}`}</AppLink>
                        </span>
                    </div>
                </div>
            );
        } else {
            return (
                <div className='flex justify-center flex-col'>
                    <div className='leading-[18px] font-bold'>{raceTitle}</div>
                </div>
            );
        }
    }, [
        KolName,
        kolData?.badges,
        kolData?.kolName?.value,
        kolData?.kolThumbSq?.src,
        kolData?.score?.value,
        raceTitle,
        t,
        type,
    ]);

    return (
        <div>
            <div className='flex justify-between items-center pt-[8px]'>
                <div>{title}</div>
                {AllUpDataItem?.allUpVideoUrl?.value && (
                    <div className='relative'>
                        <img
                            className='w-[70px] h-10 rounded-[7px] ml-2 video-pop object-cover'
                            loading='lazy'
                            src={AllUpDataItem?.allUpVideoImage?.src}
                            alt=''
                            data-poster={
                                AllUpDataItem?.allUpVideoNative?.boolValue
                                    ? ''
                                    : AllUpDataItem?.allUpVideoUrl?.value
                            }
                            data-image={AllUpDataItem?.allUpVideoImage?.src}
                            onClick={() => {
                                AllUpDataItem?.allUpVideoNative?.boolValue
                                    ? videoNative(
                                          AllUpDataItem?.allUpVideoNativePip?.boolValue ?? false,
                                          AllUpDataItem?.allUpVideoUrl?.value ?? ''
                                      )
                                    : videoNoNative('KOLBetpick');
                            }}
                        ></img>

                        <VideoIcon className='absolute -bottom-[2px] -right-[6px]'></VideoIcon>
                    </div>
                )}
            </div>
            {type === 'leader' && (
                <div className='flex justify-center flex-col pt-2'>
                    <div className='leading-[18px] font-bold'>{raceTitle}</div>
                </div>
            )}
        </div>
    );
};

export default memo(AllUpHeader);
